import type { ApprovalKindLadder, ApprovalTierInput } from '@chukua/common';
import { useEffect, useState } from 'react';
import { ApiError } from '../../lib/api';
import { useToast } from '../../lib/use-toast';
import { listApprovalThresholds, updateApprovalTiers } from './approval-thresholds.api';

export type ApprovalThresholdsState =
  { status: 'loading' } | { status: 'loaded'; ladders: ApprovalKindLadder[] } | { status: 'forbidden' } | { status: 'error' };

/** Nav-hiding is UX only — a user who reaches this route without `threshold.manage` still gets a real 403 from the server. */
export function useApprovalThresholds() {
  const [state, setState] = useState<ApprovalThresholdsState>({ status: 'loading' });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;

    listApprovalThresholds()
      .then((ladders) => {
        if (!cancelled) setState({ status: 'loaded', ladders });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /** Replaces one kind's ladder, keeping the previous rows showing on failure. Returns whether it succeeded, so the drawer knows to close itself. */
  async function save(kind: string, tiers: ApprovalTierInput[]): Promise<boolean> {
    setSaveError(null);
    setSaving(true);
    try {
      const updated = await updateApprovalTiers(kind, tiers);
      setState((prev) => (prev.status === 'loaded' ? { status: 'loaded', ladders: prev.ladders.map((l) => (l.key === kind ? updated : l)) } : prev));
      toast.success(`${updated.name} thresholds updated`);
      return true;
    } catch {
      setSaveError('Could not save this ladder — check the amounts and roles, and try again.');
      return false;
    } finally {
      setSaving(false);
    }
  }

  return { state, saveError, saving, save, resetSaveError: () => setSaveError(null) };
}
